import React from "react";
import { v4 as uuidv4 } from "uuid";
import {
    APP_BACK_OVERLAY_PRIORITY,
    APP_BACK_TRANSIENT_PRIORITY,
    registerAppBackHandler,
    useAppBackHandler,
} from "@app/back";
import { FloatingLayerProvider } from "@shared/ui/overlay/floating-layer";
import { useTheme } from "@app/styles/themes";
import { registerOverlayBackHandlers } from "./overlayBackBridge";
import { OverlayContext, type OpenOverlayArgs } from "./OverlayContext";
import { OverlayHost } from "./OverlayHost";
import { getTopmostBlockingIndex, isOverlayEntryInteractive } from "./overlayStackUtils";
import { setImperativeOverlayApi } from "./imperativeOverlayApi";
import { requestOverlayDismiss, unregisterOverlayDismissHandler } from "./overlayDismissRegistry";
import type { OverlayCloseReason, OverlayEntry, OverlayHandle } from "./types";

interface OverlayProviderProps {
    portalContainer?: Element | DocumentFragment;
    children?: React.ReactNode;
}

const OVERLAY_Z_INDEX_FALLBACK = 1300;
const FLOATING_LAYER_Z_INDEX_OFFSET = 10;

function renderOverlayComponent<TProps>(Component: React.JSXElementConstructor<TProps>, props: TProps) {
    return function ImperativeOverlayComponent() {
        return React.createElement(Component, props);
    };
}

/**
 * Корневой провайдер overlay: стек window/dialog, backdrop, Escape, scroll lock, imperative API.
 */
export const OverlayProvider: React.FC<OverlayProviderProps> = ({ portalContainer = document.body, children }) => {
    const { theme } = useTheme();
    const [entries, setEntries] = React.useState<OverlayEntry[]>([]);
    const entriesRef = React.useRef(entries);
    const surfaceElementsRef = React.useRef(new Map<string, HTMLElement>());
    const floatingLayerZIndex = (theme.tokens.zIndex.overlay ?? OVERLAY_Z_INDEX_FALLBACK) + FLOATING_LAYER_Z_INDEX_OFFSET;

    entriesRef.current = entries;

    const focusRegisteredSurface = React.useCallback((id: string) => {
        const focusId = window.requestAnimationFrame(() => {
            const surfaceElement = surfaceElementsRef.current.get(id);
            const activeElement = document.activeElement;
            if (surfaceElement == null) return;
            if (activeElement instanceof HTMLElement && surfaceElement.contains(activeElement)) return;
            surfaceElement.focus({ preventScroll: true });
        });

        return () => {
            window.cancelAnimationFrame(focusId);
        };
    }, []);

    const runClosedEntrySideEffects = React.useCallback((entry: OverlayEntry, reason: OverlayCloseReason) => {
        unregisterOverlayDismissHandler(entry.id);
        surfaceElementsRef.current.delete(entry.id);
        entry.onClosed?.(reason);

        if (entry.openerElement?.isConnected !== true) return;

        const focusId = window.requestAnimationFrame(() => {
            entry.openerElement?.focus();
        });

        return () => {
            window.cancelAnimationFrame(focusId);
        };
    }, []);

    const closeOverlay = React.useCallback(
        (id: string, reason: OverlayCloseReason = "api") => {
            void reason;
            const closedEntry = entriesRef.current.find((entry) => entry.id === id);

            if (closedEntry == null) {
                return;
            }

            setEntries((prev) => {
                const currentEntry = prev.find((entry) => entry.id === id);
                if (currentEntry == null) return prev;
                return prev.filter((entry) => entry.id !== id);
            });

            runClosedEntrySideEffects(closedEntry, reason);
        },
        [runClosedEntrySideEffects],
    );

    const requestDismissOverlay = React.useCallback(
        (id: string): void => {
            if (requestOverlayDismiss(id)) {
                return;
            }

            closeOverlay(id, "dismiss");
        },
        [closeOverlay],
    );

    const closeOverlaysByOwner = React.useCallback(
        (ownerId: string) => {
            const closedEntries = entriesRef.current.filter((entry) => entry.ownerId === ownerId);

            if (closedEntries.length === 0) {
                return;
            }

            setEntries((prev) => {
                if (!prev.some((entry) => entry.ownerId === ownerId)) {
                    return prev;
                }

                return prev.filter((entry) => entry.ownerId !== ownerId);
            });

            closedEntries.forEach((entry) => {
                runClosedEntrySideEffects(entry, "owner-unmount");
            });
        },
        [runClosedEntrySideEffects],
    );

    const focusOverlay = React.useCallback(
        (id: string) => {
            const currentEntries = entriesRef.current;
            const currentIndex = currentEntries.findIndex((entry) => entry.id === id);
            if (currentIndex === -1) return;

            const topmostBlockingIndex = getTopmostBlockingIndex(currentEntries);
            if (!isOverlayEntryInteractive(currentIndex, topmostBlockingIndex)) {
                return;
            }

            setEntries((prev) => {
                const prevIndex = prev.findIndex((entry) => entry.id === id);
                if (prevIndex === -1 || prevIndex === prev.length - 1) return prev;
                const currentEntry = prev[prevIndex];
                return [...prev.filter((entry) => entry.id !== id), currentEntry];
            });
            focusRegisteredSurface(id);
        },
        [focusRegisteredSurface],
    );

    const isOverlayOpen = React.useCallback((id: string) => entriesRef.current.some((entry) => entry.id === id), []);

    const createHandle = React.useCallback(
        (id: string): OverlayHandle => ({
            id,
            close: () => closeOverlay(id),
            focus: () => focusOverlay(id),
            isOpen: () => isOverlayOpen(id),
        }),
        [closeOverlay, focusOverlay, isOverlayOpen],
    );

    const openOverlay = React.useCallback(
        ({
            surface,
            render,
            key,
            ownerId,
            blocking = surface === "window" ? false : true,
            placement = surface === "window" ? "fullscreen" : "center",
            reuse = key != null ? "focus-existing" : "allow-multiple",
            closeOnEscape = true,
            closeOnBackdropClick = blocking,
            onClosed,
            resolveValue,
        }: OpenOverlayArgs): OverlayHandle => {
            let resultId = uuidv4();
            let replacedEntry: OverlayEntry | null = null;
            let shouldFocusExisting = false;

            const openerElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            const nextEntry: OverlayEntry = {
                id: resultId,
                key,
                ownerId,
                surface,
                blocking,
                placement,
                closeOnEscape,
                closeOnBackdropClick,
                openerElement,
                render,
                onClosed,
                resolveValue,
            };

            setEntries((prev) => {
                const existingEntry = key != null ? prev.find((entry) => entry.key === key) : undefined;
                if (existingEntry != null && reuse === "focus-existing") {
                    resultId = existingEntry.id;
                    shouldFocusExisting = true;
                    return [...prev.filter((entry) => entry.id !== existingEntry.id), existingEntry];
                }

                const nextEntries =
                    existingEntry != null && reuse === "replace"
                        ? prev.filter((entry) => entry.id !== existingEntry.id)
                        : prev;

                if (existingEntry != null && reuse === "replace") {
                    replacedEntry = existingEntry;
                }

                return [...nextEntries, nextEntry];
            });

            if (replacedEntry != null) {
                runClosedEntrySideEffects(replacedEntry, "api");
            }

            if (shouldFocusExisting) {
                focusRegisteredSurface(resultId);
            }

            return createHandle(resultId);
        },
        [createHandle, focusRegisteredSurface, runClosedEntrySideEffects],
    );

    const closeTopmostOverlay = React.useCallback((): boolean => {
        const currentEntries = entriesRef.current;
        const topmost = currentEntries[currentEntries.length - 1];
        if (topmost == null) return false;
        requestDismissOverlay(topmost.id);
        return true;
    }, [requestDismissOverlay]);

    const registerTransientBackHandler = React.useCallback(
        (handler: () => boolean) => registerAppBackHandler(handler, { priority: APP_BACK_TRANSIENT_PRIORITY }),
        [],
    );

    const registerSurfaceElement = React.useCallback((id: string, element: HTMLElement | null) => {
        if (element == null) {
            surfaceElementsRef.current.delete(id);
            return;
        }
        surfaceElementsRef.current.set(id, element);
    }, []);

    React.useEffect(() => {
        const topmostEntry = entries[entries.length - 1];
        if (topmostEntry?.closeOnEscape !== true) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return;
            event.preventDefault();
            requestDismissOverlay(topmostEntry.id);
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [entries, requestDismissOverlay]);

    useAppBackHandler(closeTopmostOverlay, { priority: APP_BACK_OVERLAY_PRIORITY });

    React.useEffect(() => {
        const hasBlockingEntries = entries.some((entry) => entry.blocking);
        if (!hasBlockingEntries) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [entries]);

    React.useEffect(() => {
        setImperativeOverlayApi({
            openOverlay,
            openWindow: (component, props, options = {}) =>
                openOverlay({
                    surface: "window",
                    render: renderOverlayComponent(component, props),
                    ...options,
                }),
            openDialog: (component, props, options = {}) =>
                openOverlay({
                    surface: "dialog",
                    placement: "center",
                    render: renderOverlayComponent(component, props),
                    ...options,
                }),
            closeOverlay,
            closeTopmostOverlay,
            hasOpenOverlays: () => entriesRef.current.length > 0,
        });

        registerOverlayBackHandlers({
            closeTopmost: closeTopmostOverlay,
            hasOpen: () => entriesRef.current.length > 0,
        });

        return () => {
            setImperativeOverlayApi(null);
            registerOverlayBackHandlers(null);
        };
    }, [closeOverlay, closeTopmostOverlay, openOverlay]);

    React.useEffect(() => {
        return () => {
            entriesRef.current.forEach((entry) => {
                runClosedEntrySideEffects(entry, "provider-unmount");
            });
        };
    }, [runClosedEntrySideEffects]);

    const contextValue = React.useMemo(
        () => ({
            entries,
            openOverlay,
            closeOverlay,
            closeOverlaysByOwner,
            focusOverlay,
            isOverlayOpen,
            isOverlayTopmost: (id: string) => {
                const currentEntries = entriesRef.current;
                return currentEntries.length > 0 && currentEntries[currentEntries.length - 1]?.id === id;
            },
            registerSurfaceElement,
        }),
        [entries, openOverlay, closeOverlay, closeOverlaysByOwner, focusOverlay, isOverlayOpen, registerSurfaceElement],
    );

    return (
        <OverlayContext.Provider value={contextValue}>
            <FloatingLayerProvider
                portalContainer={portalContainer}
                baseZIndex={floatingLayerZIndex}
                registerBackHandler={registerTransientBackHandler}
            >
                {children}
                <OverlayHost
                    entries={entries}
                    portalContainer={portalContainer}
                    closeOverlay={closeOverlay}
                    requestDismissOverlay={requestDismissOverlay}
                    focusOverlay={focusOverlay}
                    registerSurfaceElement={registerSurfaceElement}
                />
            </FloatingLayerProvider>
        </OverlayContext.Provider>
    );
};
